// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useCancelCallLink, useCreateCallLink } from "../data/mutations";
import { notify } from "../ui/toast";
import ArrowLeft from "~icons/ph/arrow-left";
import LinkBreak from "~icons/ph/link-break";
import LinkSimple from "~icons/ph/link-simple";
import Phone from "~icons/ph/phone";
import Video from "~icons/ph/video-camera";
import type {
  CallLink,
  CallMedia,
  Person,
  RegistrationProtection,
} from "../../shared/types";
import { ActivityStatus, Avatar } from "../ui/components";
import { ShareLink } from "./guest-account";
import { CallHeading } from "./call-heading";
import { t } from "../i18n";
import { TurnstileChallenge } from "../account/turnstile";
import { EntryReveal } from "../ui/entry-reveal";
import { AsciiSkeleton } from "ascii-reveal";
import { usePageVisible } from "../ui/use-page-visible";

// Focus moves to the link or the button that replaces it.
const focusCallLink = () =>
  document.querySelector<HTMLElement>("#call-link, #create-call-link")?.focus();

export function useCallLinkActions() {
  const create = useCreateCallLink(),
    cancel = useCancelCallLink();
  // A link mutation resolves before its cache update renders, so focus moves
  // after a render of its own, which reads the latest cache.
  const [linkFocus, setLinkFocus] = useState(0);
  useEffect(() => {
    if (linkFocus) focusCallLink();
  }, [linkFocus]);
  return {
    async createCallLink() {
      try {
        await create.mutateAsync();
        setLinkFocus((n) => n + 1);
      } catch (e) {
        notify((e as Error).message);
      }
    },
    async cancelCallLink() {
      try {
        await cancel.mutateAsync();
        notify(t("تم إلغاء الرابط"), "success");
        setLinkFocus((n) => n + 1);
      } catch (e) {
        notify((e as Error).message);
      }
    },
  };
}

const linkNote =
  "أرسله إلى شخص واحد. عندما يفتحه ستسمع رنّة هنا. صالح لمدة ٢٤ ساعة.";

function CallLinkShare({ id, children }: { id: string; children?: ReactNode }) {
  return (
    <ShareLink
      label={t("رابط مكالمتك")}
      link={new URL(`/call/${id}`, location.origin).href}
      text={t("انضم إلى مكالمتي على رنّة")}
      inputId="call-link"
    >
      {children}
    </ShareLink>
  );
}

export function CallLinkPanel({
  link,
  paused,
  reachable,
  disabled,
  onCreate,
  onCancel,
  also,
  aside,
}: {
  link: CallLink | null;
  paused: boolean;
  reachable: boolean;
  disabled: boolean;
  onCreate: () => void;
  onCancel: () => void;
  // Another way to start talking, after the button or below the card.
  also?: ReactNode;
  // Sits beside the button, or in the card's first row once there is a link.
  aside?: ReactNode;
}) {
  if (!link)
    return (
      <>
        <button
          id="create-call-link"
          className="button call-link-create"
          disabled={disabled}
          aria-busy={disabled}
          onClick={onCreate}
        >
          <LinkSimple width={18} height={18} aria-hidden="true" />
          {t("إنشاء رابط مكالمة")}
        </button>
        {also}
        {aside}
      </>
    );
  return (
    <>
      <EntryReveal
        as="section"
        className="call-link-card"
        aria-label={t("رابط مكالمتك")}
        data-ascii="surface"
        paused={paused}
      >
        <div data-ascii="skip">
          <div className="call-link-heading">
            <p className="call-link-status">
              <i aria-hidden="true" />
              {t("بانتظار من يفتح الرابط")}
            </p>
            {aside}
          </div>
          <CallLinkShare id={link.id}>
            <button
              className="quiet-button call-link-cancel"
              disabled={disabled}
              onClick={onCancel}
            >
              {t("إلغاء الرابط")}
            </button>
          </CallLinkShare>
          <p className="call-link-note">{t(linkNote)}</p>
          {!reachable && (
            <p className="form-error" role="alert">
              {t(
                "حالتك الآن لا تسمح بالمكالمات. اختر «متصل» من صفحة حسابك ليصلك الاتصال.",
              )}
            </p>
          )}
        </div>
      </EntryReveal>
      {also}
    </>
  );
}

export function CallInviteLoading({ paused }: { paused: boolean }) {
  const visible = usePageVisible();
  const animate = visible && !paused;
  return (
    <div className="call-invite" aria-busy="true">
      <span className="call-invite-skeleton" aria-hidden="true">
        <AsciiSkeleton
          variant="braille"
          rows={8}
          width={88}
          height={88}
          circle
          animate={animate}
        />
        <AsciiSkeleton
          variant="braille"
          rows={2}
          width="min(320px, 70vw)"
          height={32}
          animate={animate}
        />
        <AsciiSkeleton
          variant="braille"
          rows={1}
          width={160}
          height={16}
          animate={animate}
        />
      </span>
    </div>
  );
}

export function CallInvite({
  linkId,
  paused,
  host,
  unavailable,
  failed,
  me,
  friend,
  protection,
  protectionFailed,
  busy,
  onRetry,
  onRetryProtection,
  onRestore,
  onJoin,
}: {
  linkId: string;
  paused: boolean;
  host: Person | undefined;
  unavailable: boolean;
  failed: boolean;
  me: Person | null;
  // Friends already keep in touch, so the invitation does not offer it.
  friend: boolean;
  protection: RegistrationProtection | undefined;
  protectionFailed: boolean;
  busy: boolean;
  onRetry: () => void;
  onRetryProtection: () => void;
  onRestore: () => void;
  onJoin: (media: CallMedia, name: string, token: string) => Promise<void>;
}) {
  const [name, setName] = useState(""),
    [token, setToken] = useState(""),
    [attempt, setAttempt] = useState(0),
    [error, setError] = useState("");
  // Private invitations stay out of search results.
  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex";
    document.head.appendChild(robots);
    return () => {
      robots.remove();
    };
  }, []);
  async function join(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    setError("");
    try {
      await onJoin(
        submitter?.dataset.media === "video" ? "video" : "audio",
        name,
        token,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      // Verification tokens are single-use.
      setToken("");
      setAttempt((n) => n + 1);
    }
  }
  if (unavailable)
    return (
      <div className="call-invite">
        <span className="call-invite-icon" aria-hidden="true">
          <LinkBreak width={28} height={28} />
        </span>
        <h1 id="invite-title">{t("رابط المكالمة غير متاح")}</h1>
        <p className="call-invite-lead">
          {t(
            "ربما انتهت صلاحيته أو استُخدم بالفعل. اطلب رابطًا جديدًا ممن أرسله إليك.",
          )}
        </p>
        <a className="button secondary" href="/">
          {t("الذهاب إلى رنّة")}
          <ArrowLeft
            className="directional"
            width={16}
            height={16}
            aria-hidden="true"
          />
        </a>
      </div>
    );
  if (failed)
    return (
      <div className="call-invite">
        <h1 id="invite-title">{t("تعذر تحميل الدعوة.")}</h1>
        <button className="button secondary" onClick={onRetry}>
          {t("إعادة المحاولة")}
        </button>
      </div>
    );
  if (!host) return <CallInviteLoading paused={paused} />;
  if (host.id === me?.id)
    return (
      <div className="call-invite">
        <h1 id="invite-title">{t("هذا رابط مكالمتك")}</h1>
        <p className="call-invite-lead">{t(linkNote)}</p>
        <div className="call-invite-card">
          <CallLinkShare id={linkId} />
        </div>
      </div>
    );
  // A host with call notifications on gets one when the visitor joins.
  const away = host.status !== "online",
    available = !away || !!host.call_notifications;
  return (
    <div className="call-invite">
      <Avatar person={host} large dot />
      <p className="eyebrow">{t("دعوة إلى مكالمة")}</p>
      <CallHeading name={host.name}>{"{name} يدعوك إلى مكالمة"}</CallHeading>
      <p className="call-invite-host">
        <bdi dir="ltr">@{host.username}</bdi>
        <span aria-hidden="true">·</span>
        <span className={`person-activity ${host.status}`}>
          <ActivityStatus person={host} />
        </span>
      </p>
      <form className="call-invite-card" onSubmit={join}>
        {me ? (
          <p className="call-invite-self">
            <Avatar person={me} />
            {t("ستنضم باسم {name}.", { name: me.name })}
          </p>
        ) : (
          <>
            <label>
              {t("اسمك")}
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                minLength={2}
                maxLength={40}
                required
                placeholder={t("الاسم الظاهر للأصدقاء")}
                autoComplete="nickname"
              />
            </label>
            {protectionFailed ||
            (protection?.required && !protection.siteKey) ? (
              <p className="form-error" role="alert">
                {t("التحقق غير متاح حاليًا. حاول لاحقًا.")}{" "}
                <button
                  type="button"
                  className="quiet-button"
                  onClick={onRetryProtection}
                >
                  {t("إعادة المحاولة")}
                </button>
              </p>
            ) : (
              protection?.required &&
              protection.siteKey && (
                <TurnstileChallenge
                  key={attempt}
                  siteKey={protection.siteKey}
                  onToken={setToken}
                />
              )
            )}
          </>
        )}
        {!available ? (
          <p className="call-invite-away" role="status">
            {t("{name} غير متاح حاليًا. أبقِ الصفحة مفتوحة وتحقق بعد قليل.", {
              name: host.name,
            })}{" "}
            <button type="button" className="quiet-button" onClick={onRetry}>
              {t("تحقق مجددًا")}
            </button>
          </p>
        ) : (
          away && (
            <p className="call-invite-away" role="status">
              {t("سيصل إشعار إلى {name} عند انضمامك.", { name: host.name })}
            </p>
          )
        )}
        {error && (
          <p className="form-error" role="alert">
            {t(error)}
          </p>
        )}
        <div className="call-invite-actions">
          {(["audio", "video"] as const).map((media) => (
            <button
              key={media}
              className={`button ${media === "video" ? "secondary" : ""}`}
              data-media={media}
              aria-busy={busy}
              disabled={
                busy ||
                !available ||
                (!me && (!protection || (protection.required && !token)))
              }
            >
              {media === "video" ? (
                <Video width={18} height={18} aria-hidden="true" />
              ) : (
                <Phone width={18} height={18} aria-hidden="true" />
              )}
              {t(media === "video" ? "انضم بالفيديو" : "انضم إلى المكالمة")}
            </button>
          ))}
        </div>
        {!friend && (
          <p className="call-invite-note">
            {t("بعد المكالمة يمكنكما البقاء على تواصل إن أردتما.")}
          </p>
        )}
      </form>
      {!me && (
        <>
          <p className="join-note">
            {t("لا تحتاج إلى حساب أو بريد إلكتروني أو كلمة مرور.")}
          </p>
          <button className="restore-link" onClick={onRestore}>
            {t("لديك حساب؟ سجّل الدخول")}
          </button>
        </>
      )}
    </div>
  );
}
